import { FC, SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Skeleton, Typography } from '@mui/material'

import { useRoleCheck } from '../../features/account/hooks/roleHooks'
import { RoleEnum } from '../../features/account/types/RoleEnum'
import {
  CompareMatchupIdentity,
  CompareResultBody,
  CompareResultSkeleton,
  CompareResultTabs,
  UAInsightsDialogs,
  UAInsightsErrorState,
  UAInsightsNavigationProvider,
  UAInsightsPageHeader,
  useGameCreativeDetails,
  useGenreCreativeAggregate,
  useScrollToTopOnChange,
  useStickyHeader,
  useUAInsightsActions,
  useUAInsightsParams,
} from '../../features/ua-insights'
import type { CompareMatchupTarget, CompareTarget } from '../../features/ua-insights'
import type { CompareTab } from '../../features/ua-insights/hooks/useUAInsightsNavigation'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import PageService from '../../services/PageService'

const DEFAULT_COMPARE_TAB: CompareTab = 'overview'

/**
 * Routed Compare page for UA Insights. Replaces the former full-screen compare
 * dialogs with a real page so a comparison is shareable, bookmarkable, and
 * back/forward-friendly. Two routes feed this page:
 *   - `/ua-insights/game/:gameId/compare/game/:targetGameId`  (game vs game)
 *   - `/ua-insights/game/:gameId/compare/genre/:targetGenreId` (game vs genre)
 *
 * The header mirrors the UA Insights game page sticky header (back button,
 * identity, network/month selectors, tab strip that collapses on scroll); the
 * identity is the "source vs target" matchup.
 */
const UAInsightsComparePage: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { targetGenreId } = useParams<{ targetGenreId?: string }>()
  const pageId = targetGenreId ? 'ua-insights-compare-genre' : 'ua-insights-compare-game'
  usePage(PageService.getPageWithId(pageId), 'Visited UA Insights Compare')
  useDocumentTitle(t('page_title'))
  const hasAccess = useRoleCheck(RoleEnum.ua_insights)

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">{t('no_access')}</Typography>
      </Box>
    )
  }

  return (
    <UAInsightsNavigationProvider>
      <UAInsightsComparePageContent />
    </UAInsightsNavigationProvider>
  )
}

const UAInsightsComparePageContent: FC = () => {
  const { t } = useTranslation('ua-insights')
  const {
    gameId: gameIdParam,
    targetGameId: targetGameIdParam,
    targetGenreId,
  } = useParams<{
    gameId: string
    targetGameId?: string
    targetGenreId?: string
  }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { params } = useUAInsightsParams()
  const { setCompareTab } = useUAInsightsActions()

  const sourceGameId = gameIdParam ?? null
  const isGenreMode = !!targetGenreId
  const targetGameId = !isGenreMode && targetGameIdParam ? targetGameIdParam : null

  // Hooks run unconditionally; each short-circuits on a null argument so the
  // inactive target variant is a no-op. The genre aggregate excludes the
  // source game so a source belonging to the target genre isn't counted twice.
  const sourceQuery = useGameCreativeDetails(sourceGameId)
  const targetGameQuery = useGameCreativeDetails(targetGameId)
  const targetGenreQuery = useGenreCreativeAggregate(isGenreMode ? targetGenreId ?? null : null, sourceGameId)

  const targetData = isGenreMode ? targetGenreQuery.data : targetGameQuery.data
  const targetIsLoading = isGenreMode ? targetGenreQuery.isLoading : targetGameQuery.isLoading
  const targetQuery = isGenreMode ? targetGenreQuery : targetGameQuery

  const isInitialLoading = (!sourceQuery.data && sourceQuery.isLoading) || (!targetData && targetIsLoading)

  // Either side failing leaves the comparison unrenderable, so a retry has to
  // re-run whichever queries are actually in an error state.
  const isCompareError = (sourceQuery.isError && !sourceQuery.data) || (targetQuery.isError && !targetData)
  const retryCompare = useCallback(() => {
    if (sourceQuery.isError) sourceQuery.refetch()
    if (targetQuery.isError) targetQuery.refetch()
  }, [sourceQuery, targetQuery])

  const tab: CompareTab = params.compareTab ?? DEFAULT_COMPARE_TAB

  const isCompactHeader = useStickyHeader()

  // Cross-tab deep link from the Overview's shared-tag preview: switch to the
  // Creative Tags tab and remember the clicked tag so the tab scrolls to it.
  const [pendingTagScroll, setPendingTagScroll] = useState<string | null>(null)
  const handleOpenCreativeTag = useCallback(
    (tagId: string) => {
      setCompareTab('creativeTags')
      setPendingTagScroll(tagId)
    },
    [setCompareTab]
  )

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, value: CompareTab) => {
      setCompareTab(value)
    },
    [setCompareTab]
  )

  // A tab switch triggered by a cross-tab tag deep link keeps its scroll
  // position — the Creative Tags tab scrolls itself to the linked tag instead.
  useScrollToTopOnChange(tab, () => pendingTagScroll !== null)

  // Back always returns to the source UA Insights game page, forwarding the
  // current network/month so the game page re-opens with the same scope.
  const handleBack = useCallback(() => {
    const next = new URLSearchParams()
    const network = searchParams.get('network')
    const month = searchParams.get('month')
    if (network) next.set('network', network)
    if (month) next.set('month', month)
    const search = next.toString()
    navigate({ pathname: `/ua-insights/game/${sourceGameId}`, search: search ? `?${search}` : '' })
  }, [navigate, searchParams, sourceGameId])

  const matchupTarget = useMemo<CompareMatchupTarget | null>(() => {
    if (isGenreMode) return targetGenreQuery.data ? { kind: 'genre', aggregate: targetGenreQuery.data } : null
    return targetGameQuery.data ? { kind: 'game', game: targetGameQuery.data.game } : null
  }, [isGenreMode, targetGameQuery.data, targetGenreQuery.data])

  const bodyTarget = useMemo<CompareTarget | null>(() => {
    if (isGenreMode) return targetGenreQuery.data && targetGenreId ? { kind: 'genre', data: targetGenreQuery.data, genreId: targetGenreId } : null
    return targetGameQuery.data && targetGameId != null ? { kind: 'game', data: targetGameQuery.data, gameId: targetGameId } : null
  }, [isGenreMode, targetGameQuery.data, targetGenreQuery.data, targetGameId, targetGenreId])

  return (
    <Box>
      <UAInsightsPageHeader
        compact={isCompactHeader}
        onBack={handleBack}
        backLabel={t('back_to_game')}
        disabled={isInitialLoading}
        identity={
          isInitialLoading || !sourceQuery.data || !matchupTarget ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isCompactHeader ? 1 : 1.5, flex: 1, minWidth: 0 }}>
              <Skeleton variant="rounded" width={isCompactHeader ? 26 : 38} height={isCompactHeader ? 26 : 38} sx={{ borderRadius: '22%', flexShrink: 0 }} />
              <Skeleton variant="text" width={isCompactHeader ? 120 : 160} height={isCompactHeader ? 24 : 30} />
              <Skeleton variant="text" width={20} height={20} />
              <Skeleton variant="rounded" width={isCompactHeader ? 26 : 38} height={isCompactHeader ? 26 : 38} sx={{ borderRadius: '22%', flexShrink: 0 }} />
              <Skeleton variant="text" width={isCompactHeader ? 120 : 160} height={isCompactHeader ? 24 : 30} />
            </Box>
          ) : (
            <CompareMatchupIdentity source={sourceQuery.data.game} target={matchupTarget} compact={isCompactHeader} />
          )
        }
        tabs={<CompareResultTabs activeTab={tab} onTabChange={handleTabChange} compact={isCompactHeader} disabled={isInitialLoading} />}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isInitialLoading ? (
          <CompareResultSkeleton />
        ) : isCompareError ? (
          <UAInsightsErrorState onRetry={retryCompare} />
        ) : !sourceQuery.data || !bodyTarget ? (
          <Typography variant="body2" color="text.secondary">
            {t('game_details_not_found')}
          </Typography>
        ) : (
          sourceGameId != null && (
            <CompareResultBody
              tab={tab}
              source={sourceQuery.data}
              sourceGameId={sourceGameId}
              target={bodyTarget}
              onSelectTab={setCompareTab}
              onOpenCreativeTag={handleOpenCreativeTag}
              pendingTagScroll={pendingTagScroll}
              onScrolledToTag={() => setPendingTagScroll(null)}
            />
          )
        )}
      </Box>

      <UAInsightsDialogs />
    </Box>
  )
}

export default UAInsightsComparePage
